import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  BarChart3,
  Brain,
  Briefcase,
  DollarSign,
  EyeOff,
  Flame,
  Heart,
  Lock,
  MapPin,
  Sparkles,
  TrendingUp,
  Users,
} from 'lucide-react-native';
import type { ReactNode } from 'react';
import { ActivityIndicator } from 'react-native';
import { Badge, Box, Card, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useTranslateOnFly } from '../../../hooks/useTranslateOnFly';
import { DATE_LOCALES } from '../../../lib/dateLocales';
import { colors } from '../../../theme/colors';
import { enrichmentMethodLabelKey } from '../utils/enrichmentMethodLabel';
import { ProfileDivider, ProfileInfoRow } from './ProfileInfoRow';

export interface EnrichmentData {
  score?: number;
  scoreAxis?: string;
  company?: string;
  headline?: string;
  industry?: string[];
  location?: string;
  username?: string;
  aiAnalysis?: string;
  enrichedAt?: string;
  painPoints?: string[];
  reasonScore?: {
    painPoints?: number;
    economicCapacity?: number;
    investmentMentality?: number;
    urgency?: number;
    brandFocus?: number;
    contentQuality?: number;
    digitalPresence?: number;
    marketPosition?: number;
    contentAffinity?: number;
    postingActivity?: number;
    audienceReach?: number;
    audienceInteraction?: number;
  };
  followerCount?: number;
  capturedContent?: string;
  economicCapacity?: 'High' | 'Medium' | 'Low';
  enrichmentMethod?: string;
  brandScore?: number;
  brandCapacity?: 'High' | 'Medium' | 'Low';
  brandFocus?: string[];
  contentType?: string[];
  brandDescription?: string;
  profileKind?: 'brand' | 'person';
  /** Interests analysis — personal Instagram accounts only (FLOSEAI-625). */
  interestScore?: number;
  engagementLevel?: 'High' | 'Medium' | 'Low';
  interestTopics?: string[];
  interestsSummary?: string;
  /**
   * Set when the pipeline could not read the profile, so it deliberately
   * produced no score. The card must say why — an unexplained scoreless card
   * reads as a loading bug (FLOSEAI-625).
   */
  signalGap?: 'private_profile' | 'no_public_content';
}

export const ENRICHMENT_FIELD_KEYS = [
  'score',
  // What the score measured. Persisted beside it because the same number means
  // purchase potential on LinkedIn and interest readability on Instagram.
  'scoreAxis',
  'company',
  'headline',
  'industry',
  'location',
  'username',
  'aiAnalysis',
  'enrichedAt',
  'painPoints',
  'reasonScore',
  'followerCount',
  'capturedContent',
  'economicCapacity',
  'enrichmentMethod',
  'brandScore',
  'brandCapacity',
  'brandFocus',
  'contentType',
  'brandDescription',
  'profileKind',
  'interestScore',
  'engagementLevel',
  'interestTopics',
  'interestsSummary',
  'signalGap',
] as const;

function getScoreColor(score: number): string {
  if (score >= 7) return colors.status.success.main;
  if (score >= 4) return colors.status.warning.main;
  return colors.status.error.main;
}

interface EnrichmentProfileCardProps {
  data: EnrichmentData;
  /**
   * When true, the lead is an Instagram BUSINESS/BRAND profile. The card drops
   * the individual purchase-potential score gauge and frames the section as a
   * brand analysis (description, content type, niche) instead of a prospect
   * qualification. See FLOSEAI-142.
   *
   * The third variant — a personal Instagram account read for interests — is
   * driven by `data.profileKind === 'person'` rather than a prop, because the
   * pipeline already persists that discriminator (FLOSEAI-625).
   */
  isBrand?: boolean;
  /** Instagram brand bio, surfaced as the brand description. */
  bio?: string;
}

/**
 * Explains a deliberately missing score. The pipeline skips the model entirely
 * for a private or contentless profile, so there is nothing to grade — and
 * saying that plainly is more useful to a rep than a fabricated number
 * (FLOSEAI-625).
 */
function SignalGapNotice({ gap }: Readonly<{ gap: 'private_profile' | 'no_public_content' }>) {
  const { t } = useTranslation();
  const Icon = gap === 'private_profile' ? Lock : EyeOff;

  return (
    <Box
      backgroundColor={colors.status.warning.bg}
      borderRadius="md"
      padding="md"
      accessibilityRole="alert"
    >
      <HStack spacing="sm" align="center">
        <Icon size={16} color={colors.status.warning.main} strokeWidth={1.5} />
        <VStack spacing="xs" style={{ flex: 1 }}>
          <Text variant="bodySmall" color={colors.text.primary} style={{ fontWeight: '600' }}>
            {t('leads.enrichment.signalGap.title')}
          </Text>
          <Text variant="caption" color={colors.text.secondary} style={{ lineHeight: 18 }}>
            {t(`leads.enrichment.signalGap.${gap}`)}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
}

function ScoreGauge({
  score,
  variant = 'potential',
}: Readonly<{ score: number; variant?: 'potential' | 'brandFit' | 'interest' }>) {
  const { t } = useTranslation();
  const percentage = Math.round((score / 10) * 100);
  const scoreColor = getScoreColor(score);

  const getScoreBg = () => {
    if (score >= 7) return colors.status.success.bg;
    if (score >= 4) return colors.status.warning.bg;
    return colors.status.error.bg;
  };

  const getScoreLabel = () => {
    if (variant === 'brandFit') {
      if (score >= 7) return t('leads.enrichment.brandFitHigh');
      if (score >= 4) return t('leads.enrichment.brandFitMedium');
      return t('leads.enrichment.brandFitLow');
    }
    // The interest score measures how CLEARLY the interests read, not how good a
    // prospect the person is, so it must never borrow the potential wording.
    if (variant === 'interest') {
      if (score >= 7) return t('leads.enrichment.interestScoreClear');
      if (score >= 4) return t('leads.enrichment.interestScorePartial');
      return t('leads.enrichment.interestScoreUnclear');
    }
    if (score >= 7) return t('leads.enrichment.highPotential');
    if (score >= 4) return t('leads.enrichment.mediumPotential');
    return t('leads.enrichment.lowPotential');
  };

  return (
    <Box backgroundColor={getScoreBg()} borderRadius="lg" padding="md" alignItems="center">
      {variant === 'potential' && (
        <Text variant="caption" color={colors.text.tertiary} style={{ fontWeight: '600' }}>
          {t('leads.enrichment.potentialLabel')}
        </Text>
      )}
      <Text variant="h3" color={scoreColor} style={{ fontWeight: '800' }}>
        {score}/10
      </Text>
      <Text variant="caption" color={scoreColor} style={{ fontWeight: '600', marginTop: 2 }}>
        {getScoreLabel()}
      </Text>
      <Box
        backgroundColor={colors.background.secondary}
        borderRadius="full"
        style={{ width: '100%', height: 4, marginTop: 8 }}
      >
        <Box
          backgroundColor={scoreColor}
          borderRadius="full"
          style={{ width: `${percentage}%`, height: 4 }}
        />
      </Box>
    </Box>
  );
}

function ScoreBar({
  label,
  score,
  icon,
}: Readonly<{ label: string; score: number; icon: ReactNode }>) {
  const percentage = Math.round((score / 10) * 100);

  return (
    <VStack spacing="xxs">
      <HStack spacing="sm" align="center" justify="between">
        <HStack spacing="xs" align="center" style={{ flex: 1 }}>
          {icon}
          <Text variant="caption" color={colors.text.secondary} numberOfLines={1}>
            {label}
          </Text>
        </HStack>
        <Text variant="caption" color={colors.text.primary} style={{ fontWeight: '600' }}>
          {score}/10
        </Text>
      </HStack>
      <Box
        backgroundColor={colors.background.secondary}
        borderRadius="full"
        style={{ width: '100%', height: 3 }}
      >
        <Box
          backgroundColor={getScoreColor(score)}
          borderRadius="full"
          style={{ width: `${percentage}%`, height: 3 }}
        />
      </Box>
    </VStack>
  );
}

function CapacityBadge({ capacity }: Readonly<{ capacity: 'High' | 'Medium' | 'Low' }>) {
  const { t } = useTranslation();
  const colorMap = {
    High: 'green' as const,
    Medium: 'amber' as const,
    Low: 'red' as const,
  };

  return (
    <Badge variant="subtle" color={colorMap[capacity]} size="md">
      {t(`leads.enrichment.capacity${capacity}`)}
    </Badge>
  );
}

export function EnrichmentProfileCard({
  data,
  isBrand = false,
  bio,
}: Readonly<EnrichmentProfileCardProps>) {
  const { t, language } = useTranslation();
  // A personal Instagram account is read for interests, not business fit
  // (FLOSEAI-625). It is neither the LinkedIn individual variant nor the brand
  // variant, so it gets its own axes throughout.
  const isPerson = data.profileKind === 'person';
  const {
    aiAnalysis: translatedAiAnalysis,
    painPoints: translatedPainPoints,
    description: translatedBio,
    isTranslating,
  } = useTranslateOnFly({
    aiAnalysis: data.aiAnalysis,
    painPoints: data.painPoints,
    description: isPerson ? data.interestsSummary : data.brandDescription || bio,
  });

  // For brand and person leads the bio/summary is shown as its own description
  // block, so don't also surface it through the generic headline row below.
  const headline = isBrand || isPerson ? undefined : data.headline;
  const hasProfile = headline || data.company || data.location || data.followerCount;
  const hasBrandBars =
    data.reasonScore?.brandFocus != null ||
    data.reasonScore?.contentQuality != null ||
    data.reasonScore?.digitalPresence != null ||
    data.reasonScore?.marketPosition != null;
  const hasIndividualBars =
    data.reasonScore?.painPoints != null ||
    data.reasonScore?.economicCapacity != null ||
    data.reasonScore?.investmentMentality != null ||
    data.reasonScore?.urgency != null;
  const hasInterestBars =
    data.reasonScore?.contentAffinity != null ||
    data.reasonScore?.postingActivity != null ||
    data.reasonScore?.audienceReach != null ||
    data.reasonScore?.audienceInteraction != null;
  const hasScoreBreakdown = (() => {
    if (isPerson) return hasInterestBars;
    return isBrand ? hasBrandBars : hasIndividualBars;
  })();
  const hasAnalysis =
    translatedAiAnalysis ||
    (!isBrand && !isPerson && translatedPainPoints && translatedPainPoints.length > 0);
  const hasScore = (() => {
    if (isPerson) return data.interestScore != null;
    return isBrand ? data.brandScore != null : data.score != null;
  })();
  const brandDescription = isPerson
    ? translatedBio || data.interestsSummary
    : translatedBio || data.brandDescription || bio;
  const hasBio = (isBrand || isPerson) && !!brandDescription;
  const interestTopics = data.interestTopics?.filter((topic) => topic.trim().length > 0) ?? [];
  const hasInterestTopics = isPerson && interestTopics.length > 0;
  const signalGap = data.signalGap;

  // Don't render an empty-header strip when there's no meaningful content.
  // Without this the card shows just the sparkle + "Lead Enrichment" title
  // with nothing below — looks like a broken section on the detail screen.
  // A signal gap IS meaningful content: it explains the missing score.
  if (
    !hasProfile &&
    !hasScoreBreakdown &&
    !hasAnalysis &&
    !hasScore &&
    !hasBio &&
    !hasInterestTopics &&
    !signalGap
  ) {
    return null;
  }

  return (
    <Card padding="lg" radius="xl">
      <VStack spacing="md">
        {/* Header */}
        <HStack spacing="sm" align="center">
          <Sparkles size={18} color={colors.primary[500]} strokeWidth={2} />
          <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
            {(() => {
              if (isPerson) return t('leads.enrichment.interestsAnalysisTitle');
              return isBrand
                ? t('leads.enrichment.brandAnalysisTitle')
                : t('leads.enrichment.title');
            })()}
          </Text>
        </HStack>

        {/* Brand bio, or the person's interests summary */}
        {hasBio && (
          <VStack spacing="xs">
            <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
              {isPerson ? t('leads.enrichment.interestsSummary') : t('leads.enrichment.brandBio')}
            </Text>
            <Text variant="bodySmall" color={colors.text.secondary} style={{ lineHeight: 20 }}>
              {brandDescription}
            </Text>
          </VStack>
        )}

        {/* Why there is no score. Stated explicitly, never left to inference. */}
        {signalGap && <SignalGapNotice gap={signalGap} />}

        {/* Score — buyer potential for individuals, brand fit for brands, and
            for a person how clearly their interests could be read. */}
        {!isBrand && !isPerson && data.score != null && <ScoreGauge score={data.score} />}
        {isBrand && data.brandScore != null && (
          <ScoreGauge score={data.brandScore} variant="brandFit" />
        )}
        {isPerson && data.interestScore != null && (
          <ScoreGauge score={data.interestScore} variant="interest" />
        )}

        {/* Recurring themes the interests analysis identified */}
        {hasInterestTopics && (
          <VStack spacing="xs">
            <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
              {t('leads.enrichment.interestTopics')}
            </Text>
            <HStack spacing="xs" style={{ flexWrap: 'wrap' }}>
              {interestTopics.map((topic) => (
                <Badge key={topic} variant="subtle" color="purple" size="sm">
                  {topic}
                </Badge>
              ))}
            </HStack>
          </VStack>
        )}

        {/* Engagement level — the person-side analogue of brand capacity */}
        {isPerson && data.engagementLevel && (
          <HStack spacing="sm" align="center" justify="between">
            <HStack spacing="xs" align="center">
              <Heart size={14} color={colors.accent.purple[400]} strokeWidth={1.5} />
              <Text variant="caption" color={colors.text.secondary}>
                {t('leads.enrichment.engagementLevel')}
              </Text>
            </HStack>
            <CapacityBadge capacity={data.engagementLevel} />
          </HStack>
        )}

        {/* Profile Overview */}
        {hasProfile && (
          <VStack spacing="sm">
            {headline && (
              <Text variant="bodySmall" color={colors.text.secondary} style={{ lineHeight: 20 }}>
                {headline}
              </Text>
            )}
            {data.company && (
              <ProfileInfoRow
                icon={<Briefcase size={14} color={colors.accent.blue[400]} strokeWidth={1.5} />}
                label={t('leads.enrichment.company')}
                value={data.company}
              />
            )}
            {data.location && (
              <ProfileInfoRow
                icon={<MapPin size={14} color={colors.accent.amber[400]} strokeWidth={1.5} />}
                label={t('leads.enrichment.location')}
                value={data.location}
              />
            )}
            {data.followerCount != null && (
              <ProfileInfoRow
                icon={<Users size={14} color={colors.primary[400]} strokeWidth={1.5} />}
                label={t('leads.enrichment.followers')}
                value={data.followerCount.toLocaleString()}
              />
            )}
            {data.industry && data.industry.length > 0 && (
              <VStack spacing="xs">
                <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
                  {isBrand ? t('leads.enrichment.brandNiche') : t('leads.enrichment.industry')}
                </Text>
                <HStack spacing="xs" style={{ flexWrap: 'wrap' }}>
                  {data.industry.map((ind) => (
                    <Badge key={ind} variant="subtle" color="blue" size="sm">
                      {ind}
                    </Badge>
                  ))}
                </HStack>
              </VStack>
            )}
          </VStack>
        )}

        {/* Content type — Instagram brand signal */}
        {isBrand && data.contentType && data.contentType.length > 0 && (
          <VStack spacing="xs">
            <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
              {t('leads.enrichment.contentType')}
            </Text>
            <HStack spacing="xs" style={{ flexWrap: 'wrap' }}>
              {data.contentType.map((type) => (
                <Badge key={type} variant="subtle" color="purple" size="sm">
                  {type}
                </Badge>
              ))}
            </HStack>
          </VStack>
        )}

        {/* Brand Capacity — brand leads only */}
        {isBrand && data.brandCapacity && (
          <>
            <ProfileDivider />
            <HStack spacing="sm" align="center" justify="between">
              <HStack spacing="xs" align="center">
                <DollarSign size={14} color={colors.accent.amber[400]} strokeWidth={2} />
                <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '500' }}>
                  {t('leads.enrichment.brandCapacity')}
                </Text>
              </HStack>
              <CapacityBadge capacity={data.brandCapacity} />
            </HStack>
          </>
        )}

        {/* Economic Capacity — individual prospects only */}
        {!isBrand && data.economicCapacity && (
          <>
            <ProfileDivider />
            <HStack spacing="sm" align="center" justify="between">
              <HStack spacing="xs" align="center">
                <DollarSign size={14} color={colors.accent.amber[400]} strokeWidth={2} />
                <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '500' }}>
                  {t('leads.enrichment.economicCapacity')}
                </Text>
              </HStack>
              <CapacityBadge capacity={data.economicCapacity} />
            </HStack>
          </>
        )}

        {/* Score Breakdown */}
        {hasScoreBreakdown && (
          <>
            <ProfileDivider />
            <VStack spacing="sm">
              <HStack spacing="xs" align="center">
                <BarChart3 size={14} color={colors.primary[400]} strokeWidth={2} />
                <Text
                  variant="bodySmall"
                  color={colors.text.secondary}
                  style={{ fontWeight: '500' }}
                >
                  {t('leads.enrichment.scoreBreakdown')}
                </Text>
              </HStack>
              {!isBrand && !isPerson && data.reasonScore?.painPoints != null && (
                <ScoreBar
                  label={t('leads.enrichment.painPointsScore')}
                  score={data.reasonScore?.painPoints}
                  icon={
                    <AlertTriangle size={12} color={colors.status.error.main} strokeWidth={1.5} />
                  }
                />
              )}
              {!isBrand && !isPerson && data.reasonScore?.economicCapacity != null && (
                <ScoreBar
                  label={t('leads.enrichment.economicScore')}
                  score={data.reasonScore?.economicCapacity}
                  icon={<DollarSign size={12} color={colors.accent.amber[400]} strokeWidth={1.5} />}
                />
              )}
              {!isBrand && !isPerson && data.reasonScore?.investmentMentality != null && (
                <ScoreBar
                  label={t('leads.enrichment.investmentScore')}
                  score={data.reasonScore?.investmentMentality}
                  icon={
                    <TrendingUp size={12} color={colors.status.success.main} strokeWidth={1.5} />
                  }
                />
              )}
              {!isBrand && !isPerson && data.reasonScore?.urgency != null && (
                <ScoreBar
                  label={t('leads.enrichment.urgencyScore')}
                  score={data.reasonScore?.urgency}
                  icon={<Flame size={12} color={colors.accent.amber[500]} strokeWidth={1.5} />}
                />
              )}
              {isBrand && data.reasonScore?.brandFocus != null && (
                <ScoreBar
                  label={t('leads.enrichment.brandFocusScore')}
                  score={data.reasonScore?.brandFocus}
                  icon={<Sparkles size={12} color={colors.primary[400]} strokeWidth={1.5} />}
                />
              )}
              {isBrand && data.reasonScore?.contentQuality != null && (
                <ScoreBar
                  label={t('leads.enrichment.contentQualityScore')}
                  score={data.reasonScore?.contentQuality}
                  icon={<BarChart3 size={12} color={colors.accent.purple[400]} strokeWidth={1.5} />}
                />
              )}
              {isBrand && data.reasonScore?.digitalPresence != null && (
                <ScoreBar
                  label={t('leads.enrichment.digitalPresenceScore')}
                  score={data.reasonScore?.digitalPresence}
                  icon={<Users size={12} color={colors.accent.blue[400]} strokeWidth={1.5} />}
                />
              )}
              {isBrand && data.reasonScore?.marketPosition != null && (
                <ScoreBar
                  label={t('leads.enrichment.marketPositionScore')}
                  score={data.reasonScore?.marketPosition}
                  icon={
                    <TrendingUp size={12} color={colors.status.success.main} strokeWidth={1.5} />
                  }
                />
              )}
              {isPerson && data.reasonScore?.contentAffinity != null && (
                <ScoreBar
                  label={t('leads.enrichment.contentAffinityScore')}
                  score={data.reasonScore?.contentAffinity}
                  icon={<Heart size={12} color={colors.accent.purple[400]} strokeWidth={1.5} />}
                />
              )}
              {isPerson && data.reasonScore?.postingActivity != null && (
                <ScoreBar
                  label={t('leads.enrichment.postingActivityScore')}
                  score={data.reasonScore?.postingActivity}
                  icon={<BarChart3 size={12} color={colors.primary[400]} strokeWidth={1.5} />}
                />
              )}
              {isPerson && data.reasonScore?.audienceReach != null && (
                <ScoreBar
                  label={t('leads.enrichment.audienceReachScore')}
                  score={data.reasonScore?.audienceReach}
                  icon={<Users size={12} color={colors.accent.blue[400]} strokeWidth={1.5} />}
                />
              )}
              {isPerson && data.reasonScore?.audienceInteraction != null && (
                <ScoreBar
                  label={t('leads.enrichment.audienceInteractionScore')}
                  score={data.reasonScore?.audienceInteraction}
                  icon={
                    <TrendingUp size={12} color={colors.status.success.main} strokeWidth={1.5} />
                  }
                />
              )}
            </VStack>
          </>
        )}

        {/* AI Analysis */}
        {data.aiAnalysis && (
          <>
            <ProfileDivider />
            <VStack spacing="sm">
              <HStack spacing="xs" align="center">
                <Brain size={14} color={colors.accent.purple[400]} strokeWidth={2} />
                <Text
                  variant="bodySmall"
                  color={colors.text.secondary}
                  style={{ fontWeight: '500' }}
                >
                  {isBrand ? t('leads.enrichment.brandInsight') : t('leads.enrichment.aiInsight')}
                </Text>
                {isTranslating && <ActivityIndicator size={10} color={colors.text.tertiary} />}
              </HStack>
              <Box backgroundColor={colors.background.secondary} borderRadius="md" padding="sm">
                <Text variant="caption" color={colors.text.secondary} style={{ lineHeight: 18 }}>
                  {translatedAiAnalysis ?? data.aiAnalysis}
                </Text>
              </Box>
            </VStack>
          </>
        )}

        {/* Pain Points — individual prospects only */}
        {!isBrand && data.painPoints && data.painPoints.length > 0 && (
          <>
            <ProfileDivider />
            <VStack spacing="sm">
              <HStack spacing="xs" align="center">
                <AlertTriangle size={14} color={colors.status.warning.main} strokeWidth={2} />
                <Text
                  variant="bodySmall"
                  color={colors.text.secondary}
                  style={{ fontWeight: '500' }}
                >
                  {t('leads.enrichment.painPoints')}
                </Text>
              </HStack>
              {(translatedPainPoints ?? data.painPoints).map((point, index) => (
                <HStack key={`pain-${index}`} spacing="sm" align="start">
                  <Box
                    width={4}
                    height={4}
                    borderRadius="full"
                    backgroundColor={colors.status.warning.main}
                    style={{ marginTop: 6 }}
                  />
                  <Text
                    variant="caption"
                    color={colors.text.secondary}
                    style={{ flex: 1, lineHeight: 16 }}
                  >
                    {point}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </>
        )}

        {/* Footer meta */}
        {data.enrichedAt && (
          <>
            <ProfileDivider />
            <HStack spacing="md" align="center" justify="between">
              <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
                {t('leads.enrichment.enrichedLabel')}{' '}
                {formatDistanceToNow(new Date(data.enrichedAt), {
                  addSuffix: true,
                  locale: DATE_LOCALES[language],
                })}
              </Text>
              {data.enrichmentMethod && (
                <Badge variant="subtle" color="neutral" size="sm">
                  {t(enrichmentMethodLabelKey(data.enrichmentMethod))}
                </Badge>
              )}
            </HStack>
          </>
        )}
      </VStack>
    </Card>
  );
}
